import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  ContactInfoWorkflow,
  IContactInfo,
  TravelerSelectStep,
  ContactInfoStep,
  Header,
  HotelNeedToKnowPanel,
  RoomDetails,
  getCancellationPolicyInfo,
  MobilePopoverCard,
  BackButton,
  MobileFloatingButton,
  LoadingPopup,
  B2BSpinner,
  emailRegex,
  phoneRegex,
  Icon,
  IconName,
  InformationalModal as TreesModal,
  TravelWalletSingleSelectionStep,
  AccordionCollection,
  ButtonWrap,
  CloseButtonIcon,
  HotelInformationalBanner,
  getIsFreeBreakfast,
  VoidWindowNotice,
  TripPurposePanel,
  EarnOfferBanner,
  getHSPEarnOfferDescription,
  NotificationBanner,
  BannerSeverity,
} from "halifax";
import {
  ADD_CONTACT_INFO,
  CancellationPolicyEnum,
  CancellationReason,
  CheckInPolicy,
  LodgingCollectionEnum,
} from "redmond";
import { RouteComponentProps } from "react-router-dom";

import "./styles.scss";
import { useExperimentIsVariant } from "@capone/experiments";
import queryStringParser from "query-string";
import clsx from "clsx";
import {
  CDN_ASSETS,
  DO_NOT_APPLY_REWARDS_KEY,
  getConfigTenant,
  isVesperTenant,
  useIsTravelApp,
} from "@capone/common";
import { MobilePremierCollectionBookWorkflowConnectorProps } from "./container";
import {
  PriceBreakdown,
  PremierCollectionBookPassengerSelection,
  PaymentCard,
  TitleSummaryCard,
  PremierCollectionBookMobileButton,
  PremierCollectionBookSummaryPanel,
  PriceBreakdownDropdown,
  AGENT_FEE,
  ConnectionRetrySuccessBanner,
  PriceChangeInlineMessage,
} from "../../../components";
import {
  CONTACT_INFO_SAVE,
  CONTINUE,
  CONTACT_INFO_TITLE,
  CONTACT_INFO_SUBTITLE,
  NEED_TO_KNOW_TITLE,
  NEED_TO_KNOW_DETAILS,
  getCfarSecondaryText,
  REVIEW_MY_TRIP_TEXT,
  PRICE_QUOTE_MESSAGE,
  TREES_MODAL_CTA_TEXT,
  TREES_MODAL_HEADER,
  TREES_MODAL_SUBTITLE,
  TREES_MODAL_TITLE,
  TREES_BOLDED_MODAL_CTA_TEXT,
  CHECKOUT_PRICE_BREAKDOWN_HEADER,
  EXPERIENCE_CREDIT_FAQ,
  CFAR_POLICY_TITLE,
  CFAR_POLICY_SUBTITLE,
} from "./textConstants";
import { PATH_HOME, PATH_BOOK_CONFIRMATION } from "../../../../../utils/paths";
import { ClientContext } from "../../../../../App";
import { PREMIER_COLLECTION_REWARDS_DISABLED_BANNER_TEXT } from "../../../../rewards/components/RewardsBanner/constants";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import {
  goToShop,
  goToAncillaryCustomize,
} from "../../../../shop/utils/queryStringHelpers";
import { fetchCustomerDetails } from "../../../../../api/v0/customer/fetchCustomerDetails";
import { TravelOfferSelection } from "../../TravelOfferSelection";
import PremierCollectionCheckoutBenefits from "../../PremierCollectionCheckoutBenefits";
import { CALIFORNIA_BILL_644_CANCELLATION_TEXT } from "../../../../shop/textConstants";
import {
  AVAILABLE,
  HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../../../context/experiments";
import { PaymentScheduleCard } from "../../PaymentScheduleCard";
import { parseQueryString } from "../../../../availability/utils/parseQueryString";
import dayjs from "dayjs";

export interface IMobilePremierCollectionBookWorkflowProps
  extends MobilePremierCollectionBookWorkflowConnectorProps,
    RouteComponentProps {
  isTreesModalExperiment: boolean;
  isTravelWalletOfferExperiment: boolean;
  isTravelWalletCreditsExperiment: boolean;
  isCreditAndOfferStackingExperimentV1: boolean;
  isTravelCreditHistoryExperiment: boolean;
}

export enum MobilePremierCollectionBookWorkflowStep {
  TravelerInformation,
  ContactInformation,
  TravelOfferSelection,
  RewardsAndPayment,
  Review,
}

export const MobilePremierCollectionBookWorkflow = ({
  priceQuote,
  hasNoUserPassengers,
  schedulePriceQuote,
  history,
  chosenProduct,
  selectedLodging,
  roomInfoProduct,
  chosenProductIndex,
  reservation,
  isBookingValid,
  setContactInfo,
  confirmationEmail,
  confirmationPhoneNumber,
  priceDifferenceAcknowledged,
  scheduleBook,
  priceQuoteInProgress,
  priceQuoteErrors,
  confirmationDetailsErrors,
  offers,
  fetchApplicableTravelWalletItems,
  resetPaymentCardSelectedAccounts,
  setPriceQuote,
  isTravelWalletPaymentOnly,
  creditToApply,
  restrictedCreditAmountToApply,
  credit,
  isAddOnOptionAvailable,
  ancillaries,
  fetchTravelWalletCreditHistory,
  hasCfarAttached,
  hotelCfarQuote,
  largestEarnAccount,
  isTreesModalExperiment,
  isTravelWalletOfferExperiment,
  isTravelWalletCreditsExperiment,
  isCreditAndOfferStackingExperimentV1,
  isTravelCreditHistoryExperiment,
  cancellationSummary,
  setTripPurpose,
  hasPayLater,
  isPayLater,
  setRewardsPaymentAmount,
  tripTotal,
  setCreditToApply,
  setOfferToApply,
  setSelectedRewardsAccountReferenceId,
  isTravelApp,
  setIsTravelApp,
  offerToApply,
  showRequoteSuccessBanner,
}: IMobilePremierCollectionBookWorkflowProps) => {
  const clientContext = useContext(ClientContext);
  const isVesper = isVesperTenant(getConfigTenant());
  const { sessionInfo, isAgentPortal, isRewardsDisabled } = clientContext;
  // TODO: remove this when we decide if using text message - https://hopchat.slack.com/archives/C01FX0M22MV/p1620146159000600
  const [contactInfo, setContact] = useState<IContactInfo | null>({
    phoneNumber: confirmationPhoneNumber || "",
    email: confirmationEmail || sessionInfo?.userInfo?.email || "",
  });
  const [customerDetailsLoading, setCustomerDetailsLoading] =
    React.useState<boolean>(true);
  const [checkoutStep, setCheckoutStep] =
    useState<MobilePremierCollectionBookWorkflowStep>(0);
  const [travelerWorkflowStep, setTravelerWorkflowStep] =
    useState<TravelerSelectStep>(TravelerSelectStep.Main);
  const [contactInfoStep, setContactInfoStep] = useState<ContactInfoStep>(
    ContactInfoStep.Main
  );
  const [travelOfferSelectStep, setTravelOfferSelectStep] =
    useState<TravelWalletSingleSelectionStep>(
      TravelWalletSingleSelectionStep.Main
    );
  const [openPaymentCard, setOpenPaymentCard] = useState<boolean>(false);
  const [treeModalOpen, setTreeModalOpen] = useState(false);
  const [updatedInfoOnReview, setUpdatedInfoOnReview] = useState(false);
  const [nthNightModalOpen, setNthNightModalOpen] = useState<boolean>(false);

  const expState = useExperiments();

  const californiaBill644Variant = getExperimentVariant(
    expState.experiments,
    HOTELS_CALIFORNIA_BILL_644_EXPERIMENT
  );
  const isCaliforniaBill644Experiment = React.useMemo(() => {
    return californiaBill644Variant === AVAILABLE;
  }, [californiaBill644Variant]);

  const isXmodRedesign = useMemo(
    () =>
      getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
      AVAILABLE,
    [expState.experiments]
  );

  const showFree24HourCancel =
    isCaliforniaBill644Experiment &&
    cancellationSummary?.reasons.includes(CancellationReason.CaliforniaBill644);

  const cancellationPolicyInfo = chosenProduct?.cancellationPolicy
    ? getCancellationPolicyInfo(
        chosenProduct?.cancellationPolicy,
        getCfarSecondaryText
      )
    : null;

  const isTripPurposeXpEnabled = useExperimentIsVariant(
    "corp-trip-purpose",
    AVAILABLE
  );

  const onBookPremierCollection = () => {
    const queryString = queryStringParser.parse(history.location.search);
    priceDifferenceAcknowledged || !!priceQuote
      ? scheduleBook({
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          isRecommended: queryString.recommended === "true",
          ancillaries: ancillaries,
          initialTime: dayjs().toISOString(),
        })
      : schedulePriceQuote({
          history,
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          ancillaries: ancillaries,
        });
  };

  const mergedPolicies = React.useMemo(
    () =>
      [
        ...(chosenProduct?.policies ?? []),
        ...(priceQuote?.checkInInstructions.policies ?? []),
      ].reduce((uniquePolicies, currentPolicy) => {
        if (
          !uniquePolicies.some(({ title }) => currentPolicy.title === title)
        ) {
          uniquePolicies.push(currentPolicy);
        }
        return uniquePolicies;
      }, [] as CheckInPolicy[]),
    [chosenProduct?.policies, priceQuote?.checkInInstructions.policies]
  );

  const openPremierCollectionBookPassengerSelection = () =>
    setTravelerWorkflowStep(TravelerSelectStep.TravelerSelect);

  const openContactInfoWorkflow = () => {
    setContactInfoStep(ContactInfoStep.ContactInfoForm);
  };

  const openTravelOffersSelection = () => {
    setTravelOfferSelectStep(
      TravelWalletSingleSelectionStep.TravelWalletSelection
    );
  };

  const openPaymentSelection = () => setOpenPaymentCard(true);

  const incrementCheckoutStep = () => {
    setCheckoutStep((step) => {
      const searchParams = new URLSearchParams(history.location.search);
      if (
        (step === MobilePremierCollectionBookWorkflowStep.ContactInformation &&
          isCreditAndOfferStackingExperimentV1) ||
        (step === MobilePremierCollectionBookWorkflowStep.ContactInformation &&
          (isTravelWalletCreditsExperiment ? !credit : true) &&
          (isTravelWalletOfferExperiment ? !offers?.length : true)) ||
        (step ===
          MobilePremierCollectionBookWorkflowStep.TravelOfferSelection &&
          isTravelWalletPaymentOnly)
      ) {
        if (isTravelApp) {
          searchParams.set("bookStep", (step + 2).toString());

          history.push({
            ...history.location,
            search: searchParams.toString(),
          });
        }

        return step + 2;
      }
      if (isTravelApp) {
        searchParams.set(
          "bookStep",
          (step === MobilePremierCollectionBookWorkflowStep.Review
            ? step
            : step + 1
          ).toString()
        );

        history.push({
          ...history.location,
          search: searchParams.toString(),
        });
      }

      return step === MobilePremierCollectionBookWorkflowStep.Review
        ? step
        : step + 1;
    });
  };

  const handleGoBack = () => {
    if (isTravelApp) {
      history.goBack();
      return;
    }

    switch (checkoutStep) {
      case MobilePremierCollectionBookWorkflowStep.TravelerInformation:
        if (selectedLodging) {
          if (isAddOnOptionAvailable) {
            goToAncillaryCustomize({ history });
          } else {
            goToShop({ history });
          }
        } else {
          history.push(PATH_HOME);
        }
        break;
      case MobilePremierCollectionBookWorkflowStep.RewardsAndPayment:
        if (
          isCreditAndOfferStackingExperimentV1 ||
          (isTravelWalletOfferExperiment && offers?.length) ||
          (isTravelWalletCreditsExperiment && !credit)
        ) {
          setCheckoutStep((step) => step - 1);
        } else {
          setCheckoutStep((step) => step - 2);
        }
        break;
      case MobilePremierCollectionBookWorkflowStep.Review:
        if (isTravelWalletPaymentOnly) {
          setCheckoutStep((step) => step - 2);
        } else {
          setCheckoutStep((step) => step - 1);
        }
        break;
      default:
        setCheckoutStep((step) => step - 1);
        break;
    }
  };

  const closeAllPopovers = () => {
    setTravelerWorkflowStep(TravelerSelectStep.Main);
    setContactInfoStep(ContactInfoStep.Main);
    setTravelOfferSelectStep(TravelWalletSingleSelectionStep.Main);
    setOpenPaymentCard(false);
  };

  const startSchedulePriceQuoteAndResetPayment = () => {
    schedulePriceQuote({
      history,
      agentFee: isAgentPortal ? AGENT_FEE : 0,
      pollQuoteOnly: true,
      ancillaries: ancillaries,
    });
    resetPaymentCardSelectedAccounts();
    setUpdatedInfoOnReview(true);
  };

  useEffect(() => {
    if (priceQuoteErrors.length > 0 || confirmationDetailsErrors.length > 0) {
      setCheckoutStep(
        MobilePremierCollectionBookWorkflowStep.TravelerInformation
      );
    }
  }, [priceQuoteErrors.length, confirmationDetailsErrors.length]);

  // CT1261T-419: after a connection-retry requote succeeds, advance off
  // TravelerInformation (the error above demoted it) to the payment step,
  // mirroring desktop. Keyed on priceQuoteErrors clearing so a second requote
  // re-fires; the offer step is skipped under the same conditions
  // incrementCheckoutStep uses.
  useEffect(() => {
    if (!showRequoteSuccessBanner || priceQuoteErrors.length > 0) return;

    const shouldSkipTravelOfferSelectionStep =
      isCreditAndOfferStackingExperimentV1 ||
      ((isTravelWalletCreditsExperiment ? !credit : true) &&
        (isTravelWalletOfferExperiment ? !offers?.length : true));

    const requoteReturnStep = shouldSkipTravelOfferSelectionStep
      ? MobilePremierCollectionBookWorkflowStep.RewardsAndPayment
      : MobilePremierCollectionBookWorkflowStep.TravelOfferSelection;

    if (isTravelApp) {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set("bookStep", requoteReturnStep.toString());
      history.push({
        ...history.location,
        search: searchParams.toString(),
      });
    }

    setCheckoutStep(requoteReturnStep);
  }, [priceQuoteErrors.length, showRequoteSuccessBanner]);

  useEffect(() => {
    switch (checkoutStep) {
      case MobilePremierCollectionBookWorkflowStep.TravelerInformation:
        closeAllPopovers();
        openPremierCollectionBookPassengerSelection();
        break;
      case MobilePremierCollectionBookWorkflowStep.ContactInformation:
        closeAllPopovers();
        openContactInfoWorkflow();
        break;
      case MobilePremierCollectionBookWorkflowStep.TravelOfferSelection:
        closeAllPopovers();
        openTravelOffersSelection();
        break;
      case MobilePremierCollectionBookWorkflowStep.RewardsAndPayment:
        closeAllPopovers();
        openPaymentSelection();
        break;
      default:
        break;
    }
  }, [checkoutStep]);

  // note: it handles the PassengerSelection step logic separately from the previous useEffect;
  // when travelerWorkflowStep is being changed too rapidly (e.g.: changing from Main -> TravelerSelect -> TravelerInfoForm because of hasUserPassengers value)
  // it seems to cause some unexpected behaviours on TravelerSelectWorkflow, which in turn causes the Review screen to be unscrollable
  // https://hopper-jira.atlassian.net/browse/BP-1090
  useEffect(() => {
    if (
      checkoutStep ===
        MobilePremierCollectionBookWorkflowStep.TravelerInformation &&
      hasNoUserPassengers
    ) {
      setTravelerWorkflowStep(TravelerSelectStep.TravelerInfoForm);
    }
  }, [checkoutStep, hasNoUserPassengers]);

  const configurePayLaterState = () => {
    setCreditToApply(undefined);
    setOfferToApply(undefined);
    setSelectedRewardsAccountReferenceId(DO_NOT_APPLY_REWARDS_KEY);
    setRewardsPaymentAmount(DO_NOT_APPLY_REWARDS_KEY, null, null, tripTotal);
  };
  React.useEffect(() => {
    if (isPayLater) {
      configurePayLaterState();
    }
  }, [isPayLater]);

  useEffect(() => {
    if (reservation) {
      const params = isTravelApp ? "?isTravelApp=true" : "";
      history.push(`${PATH_BOOK_CONFIRMATION}${params}`);
    }
  }, [reservation]);

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search]
  );

  const travelApp = useIsTravelApp();

  useEffect(() => {
    setIsTravelApp(travelApp.isTravelApp);
  }, [travelApp.isTravelApp]);

  useEffect(() => {
    if (isTravelApp) {
      if (parsedQueryString?.bookStep === undefined) {
        // URL missing bookStep so add current Redux state to URL
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set("bookStep", checkoutStep.toString());

        history.replace({
          ...history.location,
          search: searchParams.toString(),
        });
      } else if (
        parsedQueryString?.bookStep !== undefined &&
        parsedQueryString?.bookStep !== checkoutStep
      ) {
        // URL has different bookStep because of back/forward navigation, so update Redux state
        setCheckoutStep(parsedQueryString?.bookStep);
      }
    }
  }, [checkoutStep, parsedQueryString?.bookStep, isTravelApp, history]);

  React.useEffect(() => {
    const getCustomerDetails = async () => {
      try {
        const details = await fetchCustomerDetails();
        setContact({
          email: "",
          ...contactInfo,
          phoneNumber: details?.phoneNumber || "",
        });
        setContactInfo(contactInfo?.email || "", details?.phoneNumber || "");
      } finally {
        setCustomerDetailsLoading(false);
      }
    };

    getCustomerDetails();
    isTravelWalletOfferExperiment && fetchApplicableTravelWalletItems();
    isTravelCreditHistoryExperiment && fetchTravelWalletCreditHistory();
  }, []);

  useEffect(() => {
    if (
      checkoutStep === MobilePremierCollectionBookWorkflowStep.Review &&
      !priceQuoteInProgress &&
      !!priceQuote &&
      updatedInfoOnReview
    ) {
      if (creditToApply || (restrictedCreditAmountToApply && Math.abs(restrictedCreditAmountToApply) > 0)) {
        setCheckoutStep(
          MobilePremierCollectionBookWorkflowStep.TravelOfferSelection
        ); // if user has selected a travel wallet item before PQ is run again on pax/contact info update, bring them back to that step
      } else {
        handleGoBack();
      }
    }
  }, [checkoutStep, priceQuoteInProgress, priceQuote, updatedInfoOnReview]);

  useEffect(() => {
    if (priceQuoteInProgress && !!priceQuote) {
      setPriceQuote(null, null);
    }
  }, [priceQuoteInProgress]);

  return (
    <>
      <Box
        className={clsx(
          "mobile-premier-collection-book-workflow-root",
          "display-room-details-modal",
          {
            redesigned: isVesper,
            "mobile-review-premier-collection-book":
              checkoutStep === MobilePremierCollectionBookWorkflowStep.Review,
            "free-breakfast":
              selectedLodging?.lodgingCollection ===
                LodgingCollectionEnum.Lifestyle &&
              getIsFreeBreakfast(selectedLodging?.price?.mealPlan?.kind) &&
              !selectedLodging?.nthNightPromotion,
            "nth-night": selectedLodging?.nthNightPromotion,
            "travel-app": isTravelApp,
            xmod: isXmodRedesign,
          }
        )}
      >
        {/* TODO: Header should have pricing details */}
        <Header
          className={clsx("mobile-premier-collection-book-header", "checkout")}
          left={
            !isTravelApp ? (
              <BackButton
                className="mobile-premier-collection-book-header-go-back"
                onClick={handleGoBack}
              />
            ) : undefined
          }
          right={
            <PriceBreakdownDropdown
              popoverClassName={
                checkoutStep === MobilePremierCollectionBookWorkflowStep.Review
                  ? "mobile-review-premier-collection-book-price-breakdown"
                  : undefined
              }
            />
          }
          isMobile={true}
          fullWidth={true}
        />
        <ConnectionRetrySuccessBanner />
        <PriceChangeInlineMessage />
        <TitleSummaryCard isMobile isXmodRedesign={isXmodRedesign} />
        {showFree24HourCancel && <VoidWindowNotice />}
        <PremierCollectionBookSummaryPanel
          isMobile
          className={clsx(
            "b2b",
            selectedLodging?.lodgingCollection ===
              LodgingCollectionEnum.Lifestyle
              ? "lifestyle-collection"
              : "premier-collection"
          )}
        />
        {selectedLodging?.nthNightPromotion &&
        !selectedLodging?.nthNightPromotion?.isLocked ? (
          <HotelInformationalBanner
            content={
              <>
                <ButtonWrap
                  className="nth-night-banner-tooltip"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNthNightModalOpen(true);
                  }}
                >
                  <Box className="nth-night-banner-content">
                    <Typography className="nth-night-content-text">
                      {selectedLodging?.nthNightPromotion?.description}
                    </Typography>
                    <Icon name={IconName.InfoCircle} />
                  </Box>
                </ButtonWrap>
                <MobilePopoverCard
                  className="nth-night-popover"
                  open={nthNightModalOpen}
                  onClose={() => setNthNightModalOpen(false)}
                  centered
                  topRightButton={
                    <CloseButtonIcon
                      onClick={() => setNthNightModalOpen(false)}
                    />
                  }
                  headerElement={
                    <Typography
                      variant="h2"
                      className="nth-night-popover-header-text"
                    >
                      {selectedLodging?.nthNightPromotion?.description}
                    </Typography>
                  }
                >
                  <Typography
                    variant="subtitle2"
                    className="nth-night-popover-content"
                  >
                    {selectedLodging?.nthNightPromotion?.tooltipDescription}
                  </Typography>
                </MobilePopoverCard>
              </>
            }
            isMobile
            isMap={false}
            iconName={IconName.Sparkle}
            isXmodRedesign={isXmodRedesign}
          />
        ) : null}
        {!!roomInfoProduct && chosenProductIndex !== null && (
          <RoomDetails
            roomInfoProduct={roomInfoProduct}
            productIndex={chosenProductIndex}
            lodging={selectedLodging}
            isMobile
            hideAmenities
            roomsCount={1}
            hideRoomBedDescription
            hideRoomCapacity
            showAmenitiesModal
          />
        )}
        <PremierCollectionBookPassengerSelection
          progress={travelerWorkflowStep}
          setProgress={setTravelerWorkflowStep}
          onGoBack={(travelersChanged) => {
            switch (checkoutStep) {
              case MobilePremierCollectionBookWorkflowStep.TravelerInformation:
                handleGoBack();
                break;
              case MobilePremierCollectionBookWorkflowStep.Review:
                // note: when the user changes passengers on the final step and then clicks the go-back button,
                // handle it as if the user clicked continue;
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                break;
            }
          }}
          onContinue={(travelersChanged) => {
            switch (checkoutStep) {
              case MobilePremierCollectionBookWorkflowStep.Review:
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                incrementCheckoutStep();
                break;
            }
          }}
          selectionScreenHeaderElement={<PriceBreakdownDropdown />}
          className="b2b"
          onReviewStep={
            checkoutStep === MobilePremierCollectionBookWorkflowStep.Review
          }
          isMobile={true}
          useLocalId={true}
          useXmodHeadings={isXmodRedesign}
        />
        <ContactInfoWorkflow
          titles={{
            contactInfoTitle: CONTACT_INFO_TITLE,
            contactInfoSubtitle: CONTACT_INFO_SUBTITLE,
            buttonTitle:
              checkoutStep === MobilePremierCollectionBookWorkflowStep.Review
                ? CONTACT_INFO_SAVE
                : CONTINUE,
          }}
          progressStep={contactInfoStep}
          setProgressStep={setContactInfoStep}
          isMobile={true}
          contactInfo={contactInfo}
          onGoBack={() => {
            if (
              checkoutStep ===
              MobilePremierCollectionBookWorkflowStep.ContactInformation
            ) {
              handleGoBack();
            }
          }}
          onContactInfoChange={(contactInfo) => {
            setContact(contactInfo);
            setContactInfo(contactInfo.email, contactInfo.phoneNumber);
            if (
              contactInfo.email &&
              contactInfo.phoneNumber &&
              emailRegex.test(contactInfo.email) &&
              phoneRegex.test(contactInfo.phoneNumber)
            ) {
              trackEvent({
                eventName: ADD_CONTACT_INFO,
                properties: {},
              });
            }
            incrementCheckoutStep();
          }}
          mobileHeaderElement={<PriceBreakdownDropdown />}
          className="b2b"
          onContinueClick={() => {
            switch (checkoutStep) {
              case MobilePremierCollectionBookWorkflowStep.Review:
                startSchedulePriceQuoteAndResetPayment();
                break;
              default:
                schedulePriceQuote({
                  history,
                  agentFee: isAgentPortal ? AGENT_FEE : 0,
                  pollQuoteOnly: true,
                  ancillaries,
                });
                break;
            }
          }}
          loading={customerDetailsLoading}
          bottomContent={
            showFree24HourCancel ? <VoidWindowNotice /> : undefined
          }
          hideBackButton={isTravelApp}
          useXmodHeadings={isXmodRedesign}
        />

        {hasPayLater && (
          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "payment-schedule"
            )}
          >
            <PaymentScheduleCard />
          </Box>
        )}

        {largestEarnAccount && largestEarnAccount.earn.hotelsMultiplier && (
          <PremierCollectionCheckoutBenefits
            variant={
              selectedLodging?.lodgingCollection ===
              LodgingCollectionEnum.Lifestyle
                ? "lifestyle-collection"
                : "premier-collection"
            }
            largestEarnAccount={largestEarnAccount}
            isPayLater={isPayLater}
          />
        )}
        {isTravelWalletOfferExperiment &&
          !isCreditAndOfferStackingExperimentV1 && (
            <TravelOfferSelection
              progressStep={travelOfferSelectStep}
              mobileHeaderElement={<PriceBreakdownDropdown />}
              isMobile
              onGoBack={() => {
                if (
                  checkoutStep ===
                  MobilePremierCollectionBookWorkflowStep.TravelOfferSelection
                ) {
                  handleGoBack();
                }
              }}
              onContinueClick={() => {
                incrementCheckoutStep();
                isTravelWalletPaymentOnly &&
                  setTravelOfferSelectStep(
                    TravelWalletSingleSelectionStep.Main
                  );
              }}
            />
          )}
        <Box className="mobile-hotels-price-breakdown">
          <Typography
            className="price-breakdown-header"
            variant="h4"
            component="h2"
          >
            {CHECKOUT_PRICE_BREAKDOWN_HEADER}
          </Typography>
          <PriceBreakdown />
        </Box>

        {getHSPEarnOfferDescription(offerToApply) && (
          <EarnOfferBanner isMobile />
        )}

        <AccordionCollection
          accordionContents={EXPERIENCE_CREDIT_FAQ}
          expandIcon={<Icon name="plus" />}
          collapseIcon={<Icon name="minus" />}
        />
        {hasCfarAttached ? (
          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "cancellation-details"
            )}
          >
            <Typography variant="h2" className="cfar-policy-title">
              <Icon name={IconName.CheckShieldBlue} />
              {CFAR_POLICY_TITLE(hotelCfarQuote?.coveragePercentage)}
            </Typography>
            <Typography variant="body2">
              {CFAR_POLICY_SUBTITLE(hotelCfarQuote?.coveragePercentage)}
            </Typography>
          </Box>
        ) : cancellationPolicyInfo ? (
          <Box className={"cancellation-details"}>
            <Typography variant="h4" component="h2">
              {cancellationPolicyInfo.primaryText}
            </Typography>
            <Typography variant="body2">
              {showFree24HourCancel &&
              chosenProduct?.cancellationPolicy.CancellationPolicy ===
                CancellationPolicyEnum.NonRefundable
                ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : cancellationPolicyInfo.secondaryText}
            </Typography>
          </Box>
        ) : null}
        <HotelNeedToKnowPanel
          title={NEED_TO_KNOW_TITLE}
          subtitle={NEED_TO_KNOW_DETAILS}
          policies={mergedPolicies}
          fetchingPriceQuote={priceQuoteInProgress}
          className={clsx({
            "less-padding": isTreesModalExperiment || isTripPurposeXpEnabled,
          })}
        />
        {isRewardsDisabled && (
          <NotificationBanner
            className="rewards-disabled-banner"
            label={PREMIER_COLLECTION_REWARDS_DISABLED_BANNER_TEXT}
            severity={BannerSeverity.WARNING}
          />
        )}
        {isTreesModalExperiment && (
          <TreesModal
            image={CDN_ASSETS.capone.trees.confirmation}
            header={TREES_MODAL_HEADER}
            title={TREES_MODAL_TITLE}
            onClose={() => setTreeModalOpen(false)}
            subtitle={TREES_MODAL_SUBTITLE}
            icon={<Icon className="trees-icon" name={IconName.TreesIcon} />}
            openModal={treeModalOpen}
            setOpenModal={setTreeModalOpen}
            modalLinkCopy={TREES_MODAL_CTA_TEXT}
            modalButtonCopyStartIcon={
              <Icon className="trees-icon" name={IconName.TreesIcon} />
            }
            isMobile
            boldedModalLinkCopy={TREES_BOLDED_MODAL_CTA_TEXT}
          />
        )}

        {isTripPurposeXpEnabled && (
          <TripPurposePanel setTripPurpose={setTripPurpose} isMobile />
        )}

        <PremierCollectionBookMobileButton
          hasContactInfo={!!contactInfo}
          openContactInfo={() => {
            setContactInfoStep(ContactInfoStep.ContactInfoForm);
          }}
          isBookingValid={isBookingValid}
          onConfirmAndBook={() => onBookPremierCollection()}
          // TODO: simplify/trim PremierCollectionBookMobileButton logic
          showPaymentStep={true}
          onApplyRewards={() => {}}
        />
      </Box>
      <MobilePaymentCardPopover
        openPaymentCard={openPaymentCard}
        disabled={!isBookingValid}
        onClose={() => setOpenPaymentCard(false)}
        onConfirm={() => {
          incrementCheckoutStep();
          setOpenPaymentCard(false);
        }}
        onGoBack={() => {
          if (
            checkoutStep ===
            MobilePremierCollectionBookWorkflowStep.RewardsAndPayment
          ) {
            handleGoBack();
          }
        }}
        mobileHeaderElement={<PriceBreakdownDropdown />}
        isTravelApp={isTravelApp}
      />
      <LoadingPopup
        classes={["mobile-price-quote-loading-popup"]}
        open={priceQuoteInProgress}
        message={PRICE_QUOTE_MESSAGE}
        indicator={B2BSpinner}
        indicatorSize="small"
        verticalAlignment="center"
        fullScreen={true}
      />
    </>
  );
};

interface IMobilePaymentCardPopoverProps {
  openPaymentCard: boolean;
  disabled?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onGoBack: () => void;
  mobileHeaderElement?: JSX.Element;
  isTravelApp?: boolean;
}

const MobilePaymentCardPopover = (props: IMobilePaymentCardPopoverProps) => {
  const {
    openPaymentCard,
    disabled = true,
    onClose,
    onConfirm,
    onGoBack,
    mobileHeaderElement,
    isTravelApp,
  } = props;

  return (
    <MobilePopoverCard
      open={openPaymentCard}
      className={clsx("premier-collection-payment-card-popup", "b2b")}
      fullScreen={true}
      onClose={onClose}
      topLeftButton={
        !isTravelApp ? (
          <BackButton
            className="premier-collection-payment-card-popup-back-button"
            onClick={onGoBack}
          />
        ) : undefined
      }
      headerElement={mobileHeaderElement}
    >
      <PaymentCard />
      {!disabled && (
        <MobileFloatingButton
          className="premier-collection-book-review-trip-button"
          onClick={onConfirm}
          wrapperClassName="b2b"
        >
          {REVIEW_MY_TRIP_TEXT}
        </MobileFloatingButton>
      )}
    </MobilePopoverCard>
  );
};
